import { Component, DestroyRef, inject, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '../../services/auth.service';
import { KeycloakService } from 'keycloak-angular';
import { NotificationService } from '../../services/notifications.service';
import { ICompany } from '../../model/company.model';
import { BaseService } from '../../services/base.service';
import { ProfileService } from '../../services/profile.service';
import { environment } from '../../../environments/environment';
import { CrmService } from '../../services/crm.service';

@Component({
  selector: 'app-companies-v2',
  templateUrl: './companies-v2.component.html',
  styleUrls: ['./companies-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompaniesV2Component implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly auth = inject(AuthService);
  private readonly keycloak = inject(KeycloakService);
  private readonly profile = inject(ProfileService);
  private readonly notify = inject(NotificationService);
  protected readonly baseService = inject(BaseService);
  private readonly crmService = inject(CrmService);

  companies = signal<ICompany[]>([]);
  isLoader = signal(false);
  config = signal<any>({});
  promoCode = signal('');

  ngOnInit(): void {
    this.profile.checkPhone();
    this.loadConfig();

    this.loadCompanies();
  }

  private loadCompanies(): void {
    this.isLoader.set(true);
    this.auth
      .getCompanies()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: { data: ICompany[] }) => {
          this.companies.set(res.data);
          this.isLoader.set(false);
        },
        error: () => this.isLoader.set(false),
      });
  }

  createCompany(): void {
    this.isLoader.set(true);
    this.auth
      .createCompany(this.baseService.user.referral_code ? '' : this.promoCode().trim())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.promoCode.set('');
          this.loadCompanies();
        },
        error: (res) => {
          this.isLoader.set(false);
          (res.error?.errors || []).forEach((e: any) => this.notify.showError('', e.message));
        },
      });
  }

  onSelectCompany(subdomain: string): void {
    const kc = this.keycloak.getKeycloakInstance();
    const token = kc.token;
    const refreshToken = kc.refreshToken;
    const protocol = window.location.protocol;

    let host: string;
    if (window.location.host.includes('auth')) {
      host = window.location.host.replace('auth', subdomain);
    } else if (environment.default_schema) {
      host = window.location.host;
    } else {
      switch (environment.env) {
        case 'local':
          host = window.location.host.replace('localhost', `${subdomain}.localhost`);
          break;
        case 'dev':
          host = window.location.host.replace('dev', `${subdomain}.dev`);
          break;
        case 'prod':
          host = window.location.host.replace('jecrm', `${subdomain}.jecrm`);
          break;
        default:
          host = window.location.host;
      }
    }

    window.location.href = `${protocol}//${host}/sso/${token}/${refreshToken}`;
  }

  logout(): void {
    this.auth.logout();
  }

  private loadConfig() {
    this.crmService
      .getConfig()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (config) => {
          this.config.set(config);
        },
        error: () => {},
      });
  }

  get isCreateCompany(): boolean {
    const count = (this.companies() || []).filter((v) => v.role === 'OWNER').length;
    return count < (this.config()?.max_count_company ?? 0);
  }

  readonly environment = environment;
}
